import styled, { keyframes, css } from "styled-components";
import { glassMixin, glassLightMixin } from "./Glass";

const shimmer = keyframes`
  0% {
    background-position: -400px 0;
  }
  100% {
    background-position: 400px 0;
  }
`;

const shimmerMixin = css`
  background: linear-gradient(
    90deg,
    rgba(255, 255, 255, 0.04) 25%,
    rgba(255, 255, 255, 0.1) 50%,
    rgba(255, 255, 255, 0.04) 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.8s ease-in-out infinite;
`;

const shimmerLightMixin = css`
  background: linear-gradient(
    90deg,
    rgba(26, 35, 50, 0.04) 25%,
    rgba(26, 35, 50, 0.08) 50%,
    rgba(26, 35, 50, 0.04) 75%
  );
  background-size: 800px 100%;
  animation: ${shimmer} 1.8s ease-in-out infinite;
`;

// Base skeleton block
export const SkeletonBlock = styled.div`
  ${({ $light }) => ($light ? shimmerLightMixin : shimmerMixin)}
  border-radius: ${({ theme, $radius }) => $radius || theme.borderRadius.lg};
  width: ${({ $width }) => $width || "100%"};
  height: ${({ $height }) => $height || "20px"};
`;

// Skeleton for yacht cards (matches GlassCard structure + equal-height grid)
export const SkeletonYachtCard = styled.div`
  ${({ $light }) => ($light ? glassLightMixin : glassMixin)}
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.md};
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const SkeletonImage = styled.div`
  aspect-ratio: 16 / 10;
  width: 100%;
  ${({ $light }) => ($light ? shimmerLightMixin : shimmerMixin)}
`;

const SkeletonBody = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media (max-width: 640px) {
    padding: ${({ theme }) => theme.spacing.lg};
  }
`;

const SkeletonSpecsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.md};
  border-top: 1px solid ${({ theme }) => theme.colors.lightGray};
  margin-top: auto;
  width: 100%;
`;

const SkeletonSpec = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const YachtCardSkeleton = ({ $light }) => (
  <SkeletonYachtCard $light={$light}>
    <SkeletonImage $light={$light} />
    <SkeletonBody>
      <SkeletonBlock
        $light={$light}
        $height="12px"
        $width="35%"
        style={{ marginBottom: "6px" }}
      />
      <SkeletonBlock
        $light={$light}
        $height="26px"
        $width="65%"
        style={{ marginBottom: "16px" }}
      />
      <SkeletonSpecsRow>
        <SkeletonSpec>
          <SkeletonBlock $light={$light} $height="10px" $width="36px" />
          <SkeletonBlock $light={$light} $height="14px" $width="56px" />
        </SkeletonSpec>
        <SkeletonSpec>
          <SkeletonBlock $light={$light} $height="10px" $width="40px" />
          <SkeletonBlock $light={$light} $height="14px" $width="44px" />
        </SkeletonSpec>
        <SkeletonSpec>
          <SkeletonBlock $light={$light} $height="10px" $width="44px" />
          <SkeletonBlock $light={$light} $height="14px" $width="36px" />
        </SkeletonSpec>
        <SkeletonBlock
          $light={$light}
          $height="22px"
          $width="120px"
          style={{ marginLeft: "auto" }}
        />
      </SkeletonSpecsRow>
    </SkeletonBody>
  </SkeletonYachtCard>
);

// Skeleton for brand cards (matches BrandCard structure)
const SkeletonBrandCard = styled.div`
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  overflow: hidden;
`;

const SkeletonLogoArea = styled.div`
  aspect-ratio: 16 / 9;
  ${shimmerLightMixin}
`;

const SkeletonCardBody = styled.div`
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

export const BrandCardSkeleton = () => (
  <SkeletonBrandCard>
    <SkeletonLogoArea />
    <SkeletonCardBody>
      <SkeletonBlock $light $height="10px" $width="30%" />
      <SkeletonBlock $light $height="22px" $width="55%" />
      <SkeletonBlock $light $height="14px" $width="100%" />
      <SkeletonBlock $light $height="14px" $width="85%" />
      <SkeletonBlock $light $height="12px" $width="40%" />
    </SkeletonCardBody>
  </SkeletonBrandCard>
);

// Skeleton for yacht detail page
const SkeletonDetailWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xl};
`;

const SkeletonGallery = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonMainImage = styled.div`
  ${shimmerMixin}
  aspect-ratio: 16 / 10;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
`;

const SkeletonSideImages = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SkeletonSideImage = styled.div`
  ${shimmerMixin}
  flex: 1;
  border-radius: ${({ theme }) => theme.borderRadius.xl};
`;

const SkeletonDetailContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 380px;
  gap: ${({ theme }) => theme.spacing["2xl"]};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const SkeletonSpecs = styled.div`
  ${glassMixin}
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};
`;

const SkeletonSidebar = styled.div`
  ${glassMixin}
  border-radius: ${({ theme }) => theme.borderRadius.xl};
  padding: ${({ theme }) => theme.spacing.xl};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
  height: fit-content;
`;

export const YachtDetailSkeleton = () => (
  <SkeletonDetailWrapper>
    <SkeletonBlock $height="14px" $width="200px" />
    <SkeletonBlock $height="12px" $width="30%" />
    <SkeletonBlock $height="36px" $width="50%" />
    <SkeletonGallery>
      <SkeletonMainImage />
      <SkeletonSideImages>
        <SkeletonSideImage />
        <SkeletonSideImage />
      </SkeletonSideImages>
    </SkeletonGallery>
    <SkeletonDetailContent>
      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        <SkeletonBlock $height="24px" $width="40%" />
        <SkeletonBlock $height="16px" $width="100%" />
        <SkeletonBlock $height="16px" $width="90%" />
        <SkeletonBlock $height="16px" $width="95%" />
        <SkeletonSpecs>
          {Array.from({ length: 6 }).map((_, i) => (
            <div
              key={i}
              style={{ display: "flex", flexDirection: "column", gap: "6px" }}
            >
              <SkeletonBlock $height="12px" $width="50%" />
              <SkeletonBlock $height="18px" $width="70%" />
            </div>
          ))}
        </SkeletonSpecs>
      </div>
      <SkeletonSidebar>
        <SkeletonBlock $height="32px" $width="60%" />
        <SkeletonBlock $height="44px" $width="100%" />
        <SkeletonBlock $height="44px" $width="100%" />
        <SkeletonBlock $height="44px" $width="100%" />
        <SkeletonBlock $height="100px" $width="100%" />
        <SkeletonBlock $height="48px" $width="100%" />
      </SkeletonSidebar>
    </SkeletonDetailContent>
  </SkeletonDetailWrapper>
);

// Skeleton for brands strip on homepage
const SkeletonStripWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const BrandsStripSkeleton = () => (
  <SkeletonStripWrapper>
    {Array.from({ length: 5 }).map((_, i) => (
      <SkeletonBlock $light key={i} $height="80px" $radius="12px" />
    ))}
  </SkeletonStripWrapper>
);
